import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import LoginPage from "@/components/auth/LoginPage";
import RegisterPage from "@/components/auth/RegisterPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import { CookieBanner } from "@/components/CookieBanner";
import { AccessibilityPanel } from "@/components/AccessibilityPanel";
import { DashboardWrapper } from "@/components/dashboard/DashboardWrapper";

// Lazy load non-critical routes
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const UpdatePasswordPage = lazy(() => import("./pages/UpdatePasswordPage"));
const DashboardLayout = lazy(() => import("@/components/dashboard/DashboardLayout"));
const DashboardOverview = lazy(() => import("@/components/dashboard/DashboardOverview"));
const FraudAnalysis = lazy(() => import("@/components/dashboard/FraudAnalysis"));
const FraudAnalytics = lazy(() => import("@/components/dashboard/FraudAnalytics"));
const SmartphoneServices = lazy(() => import("@/components/dashboard/SmartphoneServices"));
const CustomerBookings = lazy(() => import("@/components/dashboard/CustomerBookings"));
const AdminLayout = lazy(() => import("@/components/dashboard/admin/AdminLayout"));
const AdminDashboardOverview = lazy(() => import("@/components/dashboard/admin/AdminDashboardOverview"));
const AdminStripeSyncPage = lazy(() => import("@/components/dashboard/admin/AdminStripeSyncPage"));
const AdminCustomersOverview = lazy(() => import("@/components/dashboard/AdminCustomersOverview"));
const AdminManualCustomerManagement = lazy(() => import("@/components/dashboard/AdminManualCustomerManagement"));
const AdminBookingsManagement = lazy(() => import("@/components/dashboard/ModernAdminBookingsManagement"));
const AdminServicesManagement = lazy(() => import("@/components/dashboard/AdminServicesManagement"));
const AdminPricingManagement = lazy(() => import("@/components/dashboard/AdminPricingManagement"));
const AdminLegalManager = lazy(() => import("@/components/dashboard/AdminLegalManager"));
const AdminBackupManager = lazy(() => import("@/components/dashboard/AdminBackupManager").then(m => ({ default: m.AdminBackupManager })));
const AdminNewsletterManager = lazy(() => import("@/components/dashboard/AdminNewsletterManager"));
const AdminWelcomeEmailManager = lazy(() => import("@/components/dashboard/AdminWelcomeEmailManager"));
const SimpleEmailManager = lazy(() => import("@/components/dashboard/email-manager/SimpleEmailManager"));
const AdminPlaceholderManager = lazy(() => import("@/components/dashboard/admin/AdminPlaceholderManager").then(m => ({ default: m.AdminPlaceholderManager })));
const AdminFAQManager = lazy(() => import("@/components/dashboard/AdminFAQManager").then(m => ({ default: m.AdminFAQManager })));
const BookingAnalytics = lazy(() => import("@/components/dashboard/BookingAnalytics"));
const PasswordChangeForm = lazy(() => import("@/components/dashboard/PasswordChangeForm"));
const SubscriptionManagement = lazy(() => import("@/components/dashboard/SubscriptionManagement"));
const UserProfile = lazy(() => import("@/components/dashboard/UserProfile"));
const CompanyEmailManagement = lazy(() => import("@/components/dashboard/CompanyEmailManagement").then(m => ({ default: m.CompanyEmailManagement })));
const DailySupportTickets = lazy(() => import("@/components/dashboard/DailySupportTickets"));
const DailySupportTicketDetail = lazy(() => import("@/components/dashboard/DailySupportTicketDetail"));
const AdminDailySupportTickets = lazy(() => import("@/components/dashboard/admin/AdminDailySupportTickets"));
const AdminDailySupportTicketDetail = lazy(() => import("@/components/dashboard/admin/AdminDailySupportTicketDetail"));
const AdminRoleManagement = lazy(() => import("@/components/dashboard/admin/AdminRoleManagement"));
const AdminErrorLogs = lazy(() => import("@/components/dashboard/admin/AdminErrorLogs"));
const AdminMediaCheckUploads = lazy(() => import("@/components/dashboard/AdminMediaCheckUploads"));
const PhishingTemplateManager = lazy(() => import("@/components/dashboard/PhishingTemplateManager"));
const PhishingCampaignManager = lazy(() => import("@/components/dashboard/PhishingCampaignManager"));
const PhishingReportGenerator = lazy(() => import("@/components/dashboard/PhishingReportGenerator"));
const SecurityChecklists = lazy(() => import("@/components/dashboard/SecurityChecklists"));
const AdminChecklistsManager = lazy(() => import("@/components/dashboard/AdminChecklistsManager"));
const ServiceBetrugserkennungPage = lazy(() => import("./pages/ServiceBetrugserkennungPage"));
const ServiceHotlinePage = lazy(() => import("./pages/ServiceHotlinePage"));
const ServiceSmartphoneSetupPage = lazy(() => import("./pages/ServiceSmartphoneSetupPage"));
const ServiceAlltagsunterstuetzungPage = lazy(() => import("./pages/ServiceAlltagsunterstuetzungPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage"));
const DatenschutzPage = lazy(() => import("./pages/DatenschutzPage"));
const AGBPage = lazy(() => import("./pages/AGBPage"));
const WiderrufsrechtPage = lazy(() => import("./pages/WiderrufsrechtPage"));
const CancelAppointmentPage = lazy(() => import("./pages/CancelAppointmentPage"));
const ConfirmAppointmentPage = lazy(() => import("./pages/ConfirmAppointmentPage"));
const PhishingInfoPage = lazy(() => import("./pages/PhishingInfoPage"));
const InstallPage = lazy(() => import("./pages/InstallPage"));

const queryClient = new QueryClient();

const GoogleAnalyticsProvider = () => {
  useGoogleAnalytics();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <GoogleAnalyticsProvider />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
            <div className="min-h-screen w-full overflow-x-hidden">
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/update-password" element={<UpdatePasswordPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/agb" element={<AGBPage />} />
            <Route path="/widerrufsrecht" element={<WiderrufsrechtPage />} />
            <Route path="/services/betrugs-erkennung" element={<ServiceBetrugserkennungPage />} />
            <Route path="/services/hotline" element={<ServiceHotlinePage />} />
            <Route path="/services/smartphone-setup" element={<ServiceSmartphoneSetupPage />} />
            <Route path="/services/alltagsunterstuetzung" element={<ServiceAlltagsunterstuetzungPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/cancel-appointment/:token" element={<CancelAppointmentPage />} />
           <Route path="/confirm-appointment/:token" element={<ConfirmAppointmentPage />} />
           <Route path="/phishing-info" element={<PhishingInfoPage />} />
           <Route path="/install" element={<InstallPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <DashboardLayout />
                </DashboardWrapper>
              </ProtectedRoute>
            }>
              <Route index element={<DashboardOverview />} />
              <Route path="analysis" element={<FraudAnalysis />} />
              <Route path="services" element={<SmartphoneServices />} />
              <Route path="bookings" element={<CustomerBookings />} />
              <Route path="company-emails" element={<CompanyEmailManagement />} />
              <Route path="change-password" element={<PasswordChangeForm />} />
              <Route path="daily-support" element={<DailySupportTickets />} />
              <Route path="daily-support/:ticketId" element={<DailySupportTicketDetail />} />
              <Route path="checklists" element={<SecurityChecklists />} />
              <Route path="subscription" element={<SubscriptionManagement />} />
              <Route path="profile" element={<UserProfile />} />
              <Route path="settings" element={
                <div className="px-4 md:px-8">
                  <h1 className="text-2xl font-bold">Einstellungen</h1>
                  <div className="mt-4 space-y-4">
                    <Button asChild>
                      <a href="/dashboard/change-password">Passwort ändern</a>
                    </Button>
                  </div>
                </div>
              } />
            </Route>

            {/* Admin Routes */}
            <Route path="/dashboard/admin" element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminDashboardOverview />} />
              <Route path="customers" element={<AdminCustomersOverview />} />
              <Route path="manual-customers" element={<AdminManualCustomerManagement />} />
              <Route path="roles" element={<AdminRoleManagement />} />
              <Route path="bookings" element={<AdminBookingsManagement />} />
              <Route path="services" element={<AdminServicesManagement />} />
              <Route path="pricing" element={<AdminPricingManagement />} />
              <Route path="newsletter" element={<AdminNewsletterManager />} />
              <Route path="welcome-email" element={<AdminWelcomeEmailManager />} />
              <Route path="automated-emails" element={<SimpleEmailManager />} />
              <Route path="placeholders" element={<AdminPlaceholderManager />} />
              <Route path="faq" element={<AdminFAQManager />} />
              <Route path="legal" element={<AdminLegalManager />} />
              <Route path="backups" element={<AdminBackupManager />} />
              <Route path="stripe-sync" element={<AdminStripeSyncPage />} />
              <Route path="analytics" element={<BookingAnalytics />} />
              <Route path="fraud-analytics" element={<FraudAnalytics />} />
              <Route path="daily-support-tickets" element={<AdminDailySupportTickets />} />
              <Route path="daily-support-tickets/:ticketId" element={<AdminDailySupportTicketDetail />} />
              <Route path="phishing-templates" element={<PhishingTemplateManager />} />
              <Route path="phishing-campaigns" element={<PhishingCampaignManager />} />
              <Route path="phishing-reports" element={<PhishingReportGenerator />} />
              <Route path="error-logs" element={<AdminErrorLogs />} />
              <Route path="checklists" element={<AdminChecklistsManager />} />
              <Route path="media-check-uploads" element={<AdminMediaCheckUploads />} />
            </Route>

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
            </Routes>
            </div>
            <CookieBanner />
            <AccessibilityPanel />
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
